/* eslint-disable no-shadow, import/no-cycle */
import React, {
  useCallback, useContext, useEffect, useMemo, useRef, useState, lazy, Suspense,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import * as ReactRouterDom from 'react-router-dom';
import { parse, stringify } from 'qs';
import { message } from 'antd';

import RecordsHOC from '../../RecordsHOC';
import { referredColumns } from '../../shared/constants';
import {
  getReferredStatuses,
  getDefaultSmsTemplate,
  getDefaultOutboundCallTemplate,
  updateReferralStatusBarPreferences,
} from '../../../../actions/userProfile';
import { openModal } from '../../../../actions/modal';
import {
  REFERRED_TO_PROVIDER_FEATURE,
  hasFeatureInLocalStorage,
  verifyFeatureAccess,
} from '../../../../global/featureFlags';
import {
  printUserRecord,
  downloadZip,
  archiveReceivedRecords,
  unarchiveReceivedRecords,
  triggerAutoUpload,
  triggerSFTPUpload,
  getReferralsQuery,
  getReferralsByPage,
  getReferralStatusCounts,
  buildReferralStatusCountsQuery,
  setRecordTeam,
  setRecordTeamBulk,
} from '../../../../actions/records';
import { rowSelected } from '../../../../actions/inboxTableActions';
import { normalizeToArray } from '../../../../components/sidebar/SettingsComponents/utils/multiSelectUtils';
import { trackReferralFilterChanged } from '../../../../global/analytics';
import { resolveStickyTeamQuery, REFERRAL_TEAM_VIEW_STORAGE_KEY } from '../../shared/stickyTeamView';

import useReferralFeatureFlags from './useReferralFeatureFlags';
import useReferralActions from './useReferralActions';
import useRecordsRedesignBodyClass from '../shared/RecordsRedesign/useRecordsRedesignBodyClass';
import useStandaloneTaskSupport from './useStandaloneTaskSupport';
import useFeatureAccess from './useFeatureAccess';
import ReferralActionBar from './ReferralActionBar';
import ReferralFilterBar from './ReferralFilterBar';
import ReferralStatusCountBar from './ReferralStatusCountBar';
import ReferralRowActionsContext from './ReferralRowActionsContext';
import RowQuickActions from './RowQuickActions';
import { buildReferralColumns, buildOpenLabelModalPayload } from './buildReferralColumns';
import buildSenderColumns from '../shared/RecordsRedesign/buildSenderColumns';
import { groupReferralRows, EMPTY_EXPANDED_REFERRAL_SLUGS } from './groupReferralRows';
import '../shared/RecordsRedesign/recordsRedesign.scss';
import './referralManagement.scss';

/* global toastr */
const REFERRAL_MANAGEMENT_REDESIGN_FLAG = 'referral_management_redesign';
export const REFERRAL_STATUS_COUNTS_DEBOUNCE_MS = 400;
const STATUS_BUCKET_PARSE_OPTIONS = { ignoreQueryPrefix: true };
const LABEL_FILTER_STRINGIFY_OPTIONS = {
  format: 'RFC1738',
  addQueryPrefix: true,
  arrayFormat: 'brackets',
  sort: (a, b) => a.localeCompare(b),
};
const SCHEDULED_STATUS_NAMES = new Set(['scheduled', 'mohs scheduled']);
const LegacyReferralManagement = lazy(() => import('../ReferralManagement.legacy'));

const withoutEmptyQueryValues = (params) => (
  Object.keys(params).reduce((cleanedParams, key) => {
    const value = params[key];
    if (
      value === null
      || value === undefined
      || value === ''
      || (Array.isArray(value) && value.length === 0)
    ) {
      return cleanedParams;
    }

    return { ...cleanedParams, [key]: value };
  }, {})
);

const buildLabelFilterQuery = (search, labelId) => {
  const normalizedLabelId = labelId == null ? null : labelId.toString();
  if (!normalizedLabelId) return null;

  const params = parse(search || '', STATUS_BUCKET_PARSE_OPTIONS);
  const selectedLabels = normalizeToArray(params.tag).map((value) => value.toString());
  if (selectedLabels.includes(normalizedLabelId)) return null;

  const nextLabels = [...selectedLabels, normalizedLabelId];
  const nextParams = withoutEmptyQueryValues({ ...params, tag: nextLabels, page: 1 });

  return {
    operation: selectedLabels.length === 0 ? 'set' : 'update',
    queryString: stringify(nextParams, LABEL_FILTER_STRINGIFY_OPTIONS),
    valueCount: nextLabels.length,
  };
};

const RedirectToApp = ({ to }) => {
  if (ReactRouterDom.Redirect) return <ReactRouterDom.Redirect to={to} />;
  return null;
};

RedirectToApp.propTypes = {
  to: PropTypes.string.isRequired,
};

export const ReferralManagementRedesign = (props) => {
  const {
    selectedData,
    data,
    openModal,
    printUserRecord,
    downloadUrl,
    downloadFilename,
    downloadZip,
    archiveReceivedRecords,
    unarchiveReceivedRecords,
    triggerAutoUpload,
    triggerSFTPUpload,
    autoUploadEmr,
    currentOrganizationId,
    getDefaultSmsTemplate,
    defaultSmsTemplate,
    getDefaultOutboundCallTemplate,
    defaultOutboundCallTemplate,
    getReferredStatuses,
    updateReferralStatusBarPreferences,
    allReferredStatuses,
    referralStatusBarShowUnpinned,
    referralStatusBarPreferencesSaving,
    hasEmrIntegrationCredential,
    onOpenEhrDrawer,
    referralStatusCounts,
    referralStatusCountsLoading,
    referralStatusCountsError,
    referralStatusCountsRefreshNonce,
    referralStatusCountsTotal,
    referralStatusCountsUpdatedAt,
    getReferralStatusCounts,
    // From RecordsHOC / outer wrapper
    paramTools,
    getPageDataWithQuery,
    location,
    lastReferralRealtimeEvent,
    referralRealtimeRevision,
    inboxRefreshNonce,
    activeModalType,
    rowSelected,
    expandedReferralEntitySlugs,
    teamsEnabled,
    teams,
    onSetRecordTeamBulk,
  } = props;

  const flags = useReferralFeatureFlags(getReferredStatuses);
  const credentialSupportsStandaloneTask = useStandaloneTaskSupport(
    hasEmrIntegrationCredential,
    flags.taskCreation && !flags.hideTaskButton,
  );
  const rowActionsRef = useContext(ReferralRowActionsContext);
  const activeCountRequestController = useRef(null);
  const countRefreshTimeoutRef = useRef(null);
  const activeFilterRequestController = useRef(null);

  const groupedReferralRows = useMemo(
    () => groupReferralRows(data, expandedReferralEntitySlugs),
    [data, expandedReferralEntitySlugs],
  );

  // Bulk actions in the action bar must operate on the rows the user can
  // actually see. The prune effect below intentionally keeps Redux selection
  // state for collapsed children so re-expanding restores them, but feeding
  // those hidden rows into bulk actions (status change, send, etc.) would let
  // a user mutate records they cannot see.
  const visibleSelectedData = useMemo(() => {
    if (!Array.isArray(selectedData) || selectedData[0] === 'all') return selectedData;
    const visibleTokens = new Set(
      groupedReferralRows
        .map((row) => row?.file_token || row?.send_token)
        .filter(Boolean),
    );
    return selectedData.filter(
      (row) => visibleTokens.has(row?.file_token || row?.send_token),
    );
  }, [selectedData, groupedReferralRows]);

  useEffect(() => {
    if (!Array.isArray(selectedData) || selectedData[0] === 'all') return;
    if (selectedData.length === 0) return;
    // Skip while `data` is transiently empty (e.g. between filter/page request
    // and response). Treating that as "every row was deleted" would clobber
    // selections the user expects to survive the navigation.
    if (data.length === 0) return;

    // ENG-2514: Only prune selections for rows that are no longer in the underlying
    // dataset (genuinely deleted/refreshed away). Hidden child rows under a
    // collapsed primary stay selected so re-expanding restores them — collapsing
    // is a UI operation, not a deselection.
    const persistedRowTokens = new Set(
      data.map((row) => row?.file_token || row?.send_token).filter(Boolean),
    );
    const persistedSelectedData = selectedData.filter(
      (row) => persistedRowTokens.has(row?.file_token || row?.send_token),
    );
    if (persistedSelectedData.length === selectedData.length) return;

    rowSelected(persistedSelectedData);
  }, [data, rowSelected, selectedData]);

  const countQuery = useMemo(
    () => buildReferralStatusCountsQuery(location?.search || ''),
    [location?.search],
  );

  const currentSearchParams = useMemo(
    () => parse(location?.search || '', STATUS_BUCKET_PARSE_OPTIONS),
    [location?.search],
  );

  const selectedStatusNames = useMemo(
    () => normalizeToArray(currentSearchParams.referral_statuses),
    [currentSearchParams.referral_statuses],
  );

  const handleBucketClick = useCallback((bucket) => {
    if (!bucket || bucket.id === null || typeof bucket.id === 'undefined') return;
    if (!paramTools || typeof paramTools.push !== 'function') return;

    const { stringify, stringifyOptions, push } = paramTools;
    const liveParams = currentSearchParams;
    const normalizedBucketName = String(bucket.name || '').toLowerCase();
    const isCurrentlySelected = selectedStatusNames.some(
      (value) => String(value).toLowerCase() === normalizedBucketName,
    );

    const nextSelected = isCurrentlySelected
      ? selectedStatusNames.filter((value) => String(value).toLowerCase() !== normalizedBucketName)
      : [...selectedStatusNames, bucket.name];

    const wasScheduledActive = liveParams?.scheduled === true || liveParams?.scheduled === 'true';
    const hadScheduledFamily = selectedStatusNames.some(
      (value) => SCHEDULED_STATUS_NAMES.has(String(value).toLowerCase()),
    );
    const hasScheduledFamily = nextSelected.some(
      (value) => SCHEDULED_STATUS_NAMES.has(String(value).toLowerCase()),
    );

    const updates = {
      referral_statuses: nextSelected.length > 0 ? nextSelected : null,
    };
    if (hasScheduledFamily && !wasScheduledActive) {
      updates.scheduled = true;
    } else if (!hasScheduledFamily && wasScheduledActive && hadScheduledFamily) {
      updates.scheduled = null;
    }

    const newParams = { ...(liveParams || {}), ...updates, page: 1 };
    Object.keys(newParams).forEach((key) => {
      const value = newParams[key];
      if (
        value === null
        || value === undefined
        || value === ''
        || (Array.isArray(value) && value.length === 0)
      ) {
        delete newParams[key];
      }
    });

    let operation;
    if (selectedStatusNames.length === 0) operation = 'set';
    else if (nextSelected.length === 0) operation = 'clear';
    else operation = 'update';

    trackReferralFilterChanged({
      filterKey: 'referral_statuses',
      operation,
      source: 'status_bucket',
      valueCount: nextSelected.length,
      hasValue: nextSelected.length > 0,
    });

    const queryString = stringify(newParams, stringifyOptions);
    push(`${window.location.pathname}${queryString}`);

    if (activeFilterRequestController.current) {
      activeFilterRequestController.current.abort();
    }
    const nextRequestController = new AbortController();
    activeFilterRequestController.current = nextRequestController;
    getPageDataWithQuery(queryString, nextRequestController.signal);
  }, [paramTools, currentSearchParams, selectedStatusNames, getPageDataWithQuery]);

  const abortActiveCountRequest = useCallback(() => {
    if (activeCountRequestController.current) {
      activeCountRequestController.current.abort();
      activeCountRequestController.current = null;
    }
  }, []);

  const refreshReferralStatusCounts = useCallback(() => {
    abortActiveCountRequest();

    const nextRequestController = new AbortController();
    activeCountRequestController.current = nextRequestController;
    return getReferralStatusCounts(countQuery, nextRequestController.signal);
  }, [abortActiveCountRequest, countQuery, getReferralStatusCounts]);

  const pendingRealtimeRefresh = React.useRef(false);
  const realtimeRefreshTimer = React.useRef(null);
  const lastRealtimeRefreshAt = React.useRef(0);
  const locationSearchRef = React.useRef(location.search);
  const activeModalTypeRef = React.useRef(activeModalType);
  const previousRealtimeEvent = React.useRef(lastReferralRealtimeEvent);
  const previousRealtimeRevision = React.useRef(referralRealtimeRevision);
  const previousActiveModalType = React.useRef(activeModalType);
  const previousStatusCountsNonce = React.useRef(referralStatusCountsRefreshNonce);
  const previousModalForListRefresh = React.useRef(activeModalType);
  const pendingPostModalListRefresh = React.useRef(false);

  const shouldRefreshForActiveFilters = useCallback((event) => {
    if (!event || !event.event) {
      return false;
    }

    const params = new URLSearchParams(location.search || '');
    const ignoredKeys = new Set(['page', 'sort', 'sort_dir', 'archived']);
    const activeFilters = new Set();

    params.forEach((value, key) => {
      const normalizedKey = key.endsWith('[]') ? key.slice(0, -2) : key;
      if (ignoredKeys.has(normalizedKey)) return;
      if (value === null || value === undefined || value === '') return;
      activeFilters.add(normalizedKey);
    });

    if (activeFilters.size === 0) {
      return false;
    }

    const eventSensitiveFilters = {
      referral_status_changed: ['referral_statuses', 'due_date_start', 'due_date_end', 'overdue'],
      referral_row_changed: ['contains', 'dob', 'referral_statuses', 'assignee', 'tag'],
      referral_sms_changed: ['sms_statuses'],
      referral_reply_changed: ['reply_statuses'],
      referral_assignment_changed: ['assignee'],
    };

    const relevantFilters = eventSensitiveFilters[event.event] || [];
    return relevantFilters.some((filterKey) => activeFilters.has(filterKey));
  }, [location.search]);

  const refreshReferralsData = useCallback(async () => {
    try {
      await getPageDataWithQuery(locationSearchRef.current || '?page=1');
    } finally {
      pendingRealtimeRefresh.current = false;
    }
  }, [getPageDataWithQuery]);

  const scheduleRealtimeRefresh = useCallback(({ force = false } = {}) => {
    if (!force && activeModalTypeRef.current) {
      return;
    }

    const minRefreshIntervalMs = 3000;
    const timeSinceLastRefresh = Date.now() - lastRealtimeRefreshAt.current;
    const delayMs = Math.max(0, minRefreshIntervalMs - timeSinceLastRefresh);

    if (realtimeRefreshTimer.current) {
      clearTimeout(realtimeRefreshTimer.current);
    }

    realtimeRefreshTimer.current = setTimeout(async () => {
      realtimeRefreshTimer.current = null;
      if (!force && activeModalTypeRef.current) {
        return;
      }

      lastRealtimeRefreshAt.current = Date.now();
      await refreshReferralsData();
    }, delayMs);
  }, [refreshReferralsData]);

  useEffect(() => {
    locationSearchRef.current = location.search;
  }, [location.search]);

  useEffect(() => {
    activeModalTypeRef.current = activeModalType;
  }, [activeModalType]);

  // Refresh the referral list when a status update happens inside a modal (the
  // sideview UpdateReferral flow) and that modal is then closed. The Redux row
  // patch in FINISHED_UPDATE_REFERRAL_STATUS only matches rows that already had a
  // referral_status, and won't remove rows that the new status filters out — so
  // we re-fetch to reconcile the table with the persisted state.
  // Tracks its own previousModalForListRefresh ref instead of sharing
  // previousActiveModalType with the realtime effect below, so this stays
  // correct regardless of effect ordering.
  useEffect(() => {
    const nonceChanged = previousStatusCountsNonce.current !== referralStatusCountsRefreshNonce;
    const modalOpenNow = Boolean(activeModalType);
    const modalWasOpen = Boolean(previousModalForListRefresh.current);
    const modalJustClosed = modalWasOpen && !modalOpenNow;

    if (nonceChanged && modalOpenNow) {
      pendingPostModalListRefresh.current = true;
    }

    if (modalJustClosed && pendingPostModalListRefresh.current) {
      pendingPostModalListRefresh.current = false;
      refreshReferralsData();
    }

    previousStatusCountsNonce.current = referralStatusCountsRefreshNonce;
    previousModalForListRefresh.current = activeModalType;
  }, [referralStatusCountsRefreshNonce, activeModalType, refreshReferralsData]);

  useEffect(() => {
    if (realtimeRefreshTimer.current) {
      clearTimeout(realtimeRefreshTimer.current);
      realtimeRefreshTimer.current = null;
      pendingRealtimeRefresh.current = false;
    }
  }, [location.search]);

  useRecordsRedesignBodyClass();

  useEffect(() => () => {
    if (countRefreshTimeoutRef.current) {
      window.clearTimeout(countRefreshTimeoutRef.current);
      countRefreshTimeoutRef.current = null;
    }
    abortActiveCountRequest();
    if (activeFilterRequestController.current) {
      activeFilterRequestController.current.abort();
      activeFilterRequestController.current = null;
    }
  }, [abortActiveCountRequest]);

  useEffect(() => {
    if (flags.isLoaded && !flags.hasReferralManagementAccess) {
      toastr.error('Your plan doesn\'t include access to view the referral management inbox. Please upgrade your plan to make any changes.');
    }
  }, [flags.hasReferralManagementAccess, flags.isLoaded]);

  useEffect(() => {
    if (countRefreshTimeoutRef.current) {
      window.clearTimeout(countRefreshTimeoutRef.current);
      countRefreshTimeoutRef.current = null;
    }

    if (!flags.isLoaded || !flags.hasReferralManagementAccess) {
      abortActiveCountRequest();
      return undefined;
    }

    abortActiveCountRequest();
    countRefreshTimeoutRef.current = window.setTimeout(() => {
      countRefreshTimeoutRef.current = null;
      refreshReferralStatusCounts();
    }, REFERRAL_STATUS_COUNTS_DEBOUNCE_MS);

    return () => {
      if (countRefreshTimeoutRef.current) {
        window.clearTimeout(countRefreshTimeoutRef.current);
        countRefreshTimeoutRef.current = null;
      }
    };
  }, [
    abortActiveCountRequest,
    countQuery,
    flags.hasReferralManagementAccess,
    flags.isLoaded,
    referralStatusCountsRefreshNonce,
    // Record actions (archive / notify / send-to-chart) bump this nonce and
    // RecordsHOC silently refetches the rows; refresh the status counts too so
    // the bucket bar stays in sync with the refreshed table.
    inboxRefreshNonce,
    refreshReferralStatusCounts,
  ]);

  useEffect(() => () => {
    // Clear the shared timer ref on unmount so delayed refreshes do not leak across page exits.
    if (realtimeRefreshTimer.current) {
      clearTimeout(realtimeRefreshTimer.current);
      realtimeRefreshTimer.current = null;
    }
  }, []);

  useEffect(() => {
    if (!flags.referralRealtimeEnabled) {
      previousRealtimeEvent.current = lastReferralRealtimeEvent;
      previousRealtimeRevision.current = referralRealtimeRevision;
      previousActiveModalType.current = activeModalType;
      return;
    }

    if (previousRealtimeEvent.current !== lastReferralRealtimeEvent) {
      const event = lastReferralRealtimeEvent;
      const eventRequiresRefresh = event?.metadata?.refresh_required === true;

      // refresh_required events also bump referralRealtimeRevision in the reducer,
      // so the revision branch below handles the refresh without double-scheduling.
      if (!eventRequiresRefresh && shouldRefreshForActiveFilters(event)) {
        pendingRealtimeRefresh.current = true;
        scheduleRealtimeRefresh();
      }
    }

    if (previousRealtimeRevision.current !== referralRealtimeRevision) {
      pendingRealtimeRefresh.current = true;
      scheduleRealtimeRefresh();
    }

    if (previousActiveModalType.current && !activeModalType && pendingRealtimeRefresh.current) {
      scheduleRealtimeRefresh({ force: true });
    }

    previousRealtimeEvent.current = lastReferralRealtimeEvent;
    previousRealtimeRevision.current = referralRealtimeRevision;
    previousActiveModalType.current = activeModalType;
  }, [
    activeModalType,
    flags.referralRealtimeEnabled,
    lastReferralRealtimeEvent,
    referralRealtimeRevision,
    location.search,
    scheduleRealtimeRefresh,
    shouldRefreshForActiveFilters,
  ]);

  const actions = useReferralActions({
    selectedData: visibleSelectedData,
    // Bulk actions (Select-All Download, Forward, Send, EMR upload, Print)
    // must only see the rows the user can actually see. Passing raw `data`
    // would let Select-All operate on collapsed linked children that the
    // grouping transform hid from the table and from the visible selection.
    data: groupedReferralRows,
    openModal,
    printUserRecord,
    downloadUrl,
    downloadFilename,
    downloadZip,
    archiveReceivedRecords,
    unarchiveReceivedRecords,
    triggerAutoUpload,
    autoUploadEmr,
    currentOrganizationId,
    getDefaultSmsTemplate,
    defaultSmsTemplate,
    getDefaultOutboundCallTemplate,
    defaultOutboundCallTemplate,
    onOpenEhrDrawer,
    refreshReferrals: getPageDataWithQuery,
    canForward: flags.canForward,
    triggerSFTPUpload,
  });

  // Publish the live page-level action context so the per-row RowQuickActions menu
  // (rendered inside the sibling InboxTable) reuses the SAME handlers/flags/gating
  // as the top toolbar. Refreshed every render; read at click time.
  if (rowActionsRef) {
    rowActionsRef.current = {
      actions,
      flags,
      rowSelected,
      // Publish the SAME selection the toolbar acts on (visibleSelectedData), so
      // the per-row menu's counts + archive/merge resolution respect the
      // linked-row visibility boundary and never mutate collapsed child rows.
      selectedData: visibleSelectedData,
      data: groupedReferralRows,
      hasEmrIntegrationCredential,
      autoUploadEmr,
      allowReplyChartActions: false,
      useReferralArchiveWarnings: true,
      credentialSupportsStandaloneTask,
    };
  }

  if (!flags.isLoaded) return null;

  if (!flags.hasReferralManagementAccess) {
    return <RedirectToApp to="/app" />;
  }

  return (
    <div className="records-redesign">
      <Helmet>
        <title>Referral Management - Medsender</title>
      </Helmet>
      <ReferralActionBar
        selectedData={visibleSelectedData}
        data={groupedReferralRows}
        actions={actions}
        flags={flags}
        hasEmrIntegrationCredential={hasEmrIntegrationCredential}
        credentialSupportsStandaloneTask={credentialSupportsStandaloneTask}
        autoUploadEmr={autoUploadEmr}
        teamsEnabled={teamsEnabled}
        teams={teams}
        onSetTeamBulk={onSetRecordTeamBulk}
      />
      <ReferralStatusCountBar
        statusCounts={referralStatusCounts}
        allReferredStatuses={allReferredStatuses}
        showUnpinnedStatuses={referralStatusBarShowUnpinned}
        total={referralStatusCountsTotal}
        updatedAt={referralStatusCountsUpdatedAt}
        loading={referralStatusCountsLoading}
        hasError={referralStatusCountsError}
        savingPreferences={referralStatusBarPreferencesSaving}
        updateReferralStatusBarPreferences={updateReferralStatusBarPreferences}
        selectedStatusNames={selectedStatusNames}
        onBucketClick={handleBucketClick}
      />
      <ReferralFilterBar
        paramTools={paramTools}
        getPageDataWithQuery={getPageDataWithQuery}
        location={location}
        autoUploadToEMR={flags.autoUploadToEMR}
        createPatientChart={flags.createPatientChart}
        taskCreation={flags.taskCreation}
        hideTaskButton={flags.hideTaskButton}
      />
    </div>
  );
};

const passedProps = {
  getSelectedPageData: getReferralsByPage,
  getPageDataWithQuery: getReferralsQuery,
  data: 'received_data',
  mode: 'Referred',
  enableSelectAll: true,
  syncSelectionFromStore: true,
  // Renders the per-row quick-actions trigger INSIDE the checkbox cell (no extra
  // column). Opt-in per surface; legacy tables never pass this.
  renderSelectionCell: (checked, record, index, originNode) => (
    <RowQuickActions record={record} originNode={originNode} />
  ),
  columnTitles: referredColumns,
  markRecordAsReceived: true,
  sidebarClassName: 'referral-sidebar',
  hideSidebar: true,
  // Teams v3 (D6): seed the sticky team-view filter into the first fetch so
  // navigate-back applies the saved Unassigned/team filter without racing a
  // team-less initial fetch.
  resolveInitialQuery: (search) => resolveStickyTeamQuery(search, REFERRAL_TEAM_VIEW_STORAGE_KEY),
  // ENG-2514: synchronous row + column transforms applied during HOC render so the
  // first paint already shows grouped referral rows and the linked-row caret column
  // (no two-render flicker on mount or refresh).
  transformRows: (rows, hocProps) => (
    groupReferralRows(rows, hocProps?.expandedReferralEntitySlugs || EMPTY_EXPANDED_REFERRAL_SLUGS)
  ),
  transformColumnTitles: (columns, hocProps) => (
    buildReferralColumns(buildSenderColumns(columns, 'referrals'), {
      departments: hocProps?.departments || [],
      expandedReferralEntitySlugs: hocProps?.expandedReferralEntitySlugs || EMPTY_EXPANDED_REFERRAL_SLUGS,
      onAddLabelFilter: hocProps?.onAddLabelFilter || (() => {}),
      onOpenLabelModal: hocProps?.onOpenLabelModal || (() => {}),
      onToggleReferralEntity: hocProps?.onToggleReferralEntity || (() => {}),
      teamsEnabled: hocProps?.teamsEnabled || false,
      teams: hocProps?.teams || [],
      onSetRecordTeam: hocProps?.onSetRecordTeam || (() => {}),
      referredToProviderEnabled: hocProps?.referredToProviderEnabled || false,
    })
  ),
};

ReferralManagementRedesign.defaultProps = {
  selectedData: [],
  downloadUrl: '',
  downloadFilename: '',
  archiveErrorMessage: null,
  autoUploadEmr: '',
  defaultSmsTemplate: {},
  defaultOutboundCallTemplate: {},
  referralStatusCounts: [],
  referralStatusCountsUpdatedAt: null,
  referralStatusCountsError: false,
  allReferredStatuses: [],
  referralStatusBarShowUnpinned: true,
  paramTools: {},
  location: {},
  activeModalType: null,
  lastReferralRealtimeEvent: null,
  referralRealtimeRevision: 0,
  inboxRefreshNonce: 0,
  rowSelected: () => {},
  expandedReferralEntitySlugs: new Set(),
  teamsEnabled: false,
  teams: [],
  onSetRecordTeamBulk: () => {},
};

ReferralManagementRedesign.propTypes = {
  selectedData: PropTypes.array,
  openModal: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
  downloadUrl: PropTypes.string,
  downloadFilename: PropTypes.string,
  downloadZip: PropTypes.func.isRequired,
  printUserRecord: PropTypes.func.isRequired,
  archiveReceivedRecords: PropTypes.func.isRequired,
  unarchiveReceivedRecords: PropTypes.func.isRequired,
  triggerAutoUpload: PropTypes.func.isRequired,
  triggerSFTPUpload: PropTypes.func.isRequired,
  error: PropTypes.bool.isRequired,
  archiveErrorMessage: PropTypes.string,
  autoUploadEmr: PropTypes.string,
  currentOrganizationId: PropTypes.number.isRequired,
  getReferredStatuses: PropTypes.func.isRequired,
  hasEmrIntegrationCredential: PropTypes.bool.isRequired,
  onOpenEhrDrawer: PropTypes.func.isRequired,
  getDefaultSmsTemplate: PropTypes.func.isRequired,
  getDefaultOutboundCallTemplate: PropTypes.func.isRequired,
  defaultSmsTemplate: PropTypes.object,
  defaultOutboundCallTemplate: PropTypes.object,
  referralStatusCounts: PropTypes.array,
  referralStatusCountsLoading: PropTypes.bool.isRequired,
  referralStatusCountsError: PropTypes.bool.isRequired,
  referralStatusCountsRefreshNonce: PropTypes.number.isRequired,
  referralStatusCountsTotal: PropTypes.number.isRequired,
  referralStatusCountsUpdatedAt: PropTypes.string,
  allReferredStatuses: PropTypes.array,
  referralStatusBarShowUnpinned: PropTypes.bool.isRequired,
  referralStatusBarPreferencesSaving: PropTypes.bool.isRequired,
  updateReferralStatusBarPreferences: PropTypes.func.isRequired,
  getReferralStatusCounts: PropTypes.func.isRequired,
  paramTools: PropTypes.object,
  getPageDataWithQuery: PropTypes.func.isRequired,
  location: PropTypes.object,
  referralRealtimeRevision: PropTypes.number,
  inboxRefreshNonce: PropTypes.number,
  activeModalType: PropTypes.string,
  lastReferralRealtimeEvent: PropTypes.object,
  rowSelected: PropTypes.func,
  expandedReferralEntitySlugs: PropTypes.instanceOf(Set),
  teamsEnabled: PropTypes.bool,
  teams: PropTypes.array,
  onSetRecordTeamBulk: PropTypes.func,
};

const ConnectedReferralManagementRedesign = RecordsHOC(passedProps)(
  connect((state) => ({
    selectedData: state.inboxTable.recordData,
    downloadUrl: state.records.downloadUrl,
    downloadFilename: state.records.downloadFilename,
    data: state.records.received_data,
    archiveErrorMessage: state.records.archiveErrorMessage,
    errorMessage: state.records.errorMessage,
    autoUploadEmr: state.auth.data.autoUploadEmr,
    currentOrganizationId: state.auth.currentOrganizationId,
    defaultSmsTemplate: state.userProfile.defaultSmsTemplate,
    allReferredStatuses: state.userProfile.allReferredStatuses,
    referralStatusBarShowUnpinned: state.userProfile.referralStatusBarShowUnpinned,
    referralStatusBarPreferencesSaving: state.userProfile.referralStatusBarPreferencesSaving,
    defaultOutboundCallTemplate: state.userProfile.defaultOutboundCallTemplate,
    hasEmrIntegrationCredential: state.auth.hasEmrIntegrationCredential,
    referralStatusCounts: state.records.referralStatusCounts,
    referralStatusCountsLoading: state.records.referralStatusCountsLoading,
    referralStatusCountsError: state.records.referralStatusCountsError,
    referralStatusCountsRefreshNonce: state.records.referralStatusCountsRefreshNonce,
    referralStatusCountsTotal: state.records.referralStatusCountsTotal,
    referralStatusCountsUpdatedAt: state.records.referralStatusCountsUpdatedAt,
    referralRealtimeRevision: state.records.referralRealtimeRevision,
    lastReferralRealtimeEvent: state.records.lastReferralRealtimeEvent,
    inboxRefreshNonce: state.records.inboxRefreshNonce,
    activeModalType: state.modal.modalType?.type || null,
  }), {
    printUserRecord,
    downloadZip,
    openModal,
    archiveReceivedRecords,
    unarchiveReceivedRecords,
    getReferredStatuses,
    triggerAutoUpload,
    triggerSFTPUpload,
    getDefaultSmsTemplate,
    updateReferralStatusBarPreferences,
    getReferralStatusCounts,
    getDefaultOutboundCallTemplate,
    rowSelected,
  })(ReferralManagementRedesign),
);

// ENG-2514: hoist expand/collapse state above the HOC so the static
// transformRows/transformColumnTitles can read it on the very first HOC render.
// This eliminates the previous two-pass flicker where InboxTable rendered ungrouped
// rows for one frame before an effect-driven setTableOverrides committed.
const RedesignWithExpansionStateBase = (props) => {
  const {
    getReferralRecordsWithQuery,
    history,
    location,
    openModal: open,
    teams,
    setRecordTeam: dispatchSetRecordTeam,
    setRecordTeamBulk: dispatchSetRecordTeamBulk,
    rowSelected: clearRowSelection,
  } = props;
  const [expandedReferralEntitySlugs, setExpandedReferralEntitySlugs] = useState(
    () => new Set(),
  );
  // Wraps both the page component (writer) and the sibling InboxTable (reader) so
  // RowQuickActions can reach the live page-level action context.
  const rowActionsRef = useRef({});

  // Flag-gate the Teams column (cached flag for first paint, server-backed for
  // convergence — mirrors the redesignAccess pattern below).
  const [teamsEnabled, setTeamsEnabled] = useState(
    () => hasFeatureInLocalStorage('teams') === true,
  );
  useEffect(() => {
    let mounted = true;
    verifyFeatureAccess('teams').then((enabled) => {
      if (mounted) setTeamsEnabled(enabled === true);
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  // D7: refetch the current referral query after the tag is saved so the row and
  // any active team-view filter reflect the saved state; toast on 403/error.
  const onSetRecordTeam = useCallback((record, teamSlug) => {
    if (!record?.send_token) return;
    dispatchSetRecordTeam(record.send_token, teamSlug, record.receiver_type)
      .then((result) => {
        if (result?.error) {
          message.error(result?.response?.data?.error || 'Unable to update team. Please try again.');
          return;
        }
        if (getReferralRecordsWithQuery) {
          getReferralRecordsWithQuery(location?.search || window.location.search || '');
        }
      });
  }, [dispatchSetRecordTeam, getReferralRecordsWithQuery, location]);

  // D4: bulk set/clear team, then refetch the current query and clear the selection
  // so the rows + any active team-view filter reflect the saved state.
  const onSetRecordTeamBulk = useCallback((records, teamSlug) => (
    dispatchSetRecordTeamBulk(records, teamSlug).then((result) => {
      if (result?.error) {
        message.error(result?.response?.data?.error || 'Unable to update teams. Please try again.');
        return;
      }
      if (getReferralRecordsWithQuery) {
        getReferralRecordsWithQuery(location?.search || window.location.search || '');
      }
      clearRowSelection([]);
    })
  ), [dispatchSetRecordTeamBulk, getReferralRecordsWithQuery, location, clearRowSelection]);

  const onToggleReferralEntity = useCallback((slug) => {
    if (!slug) return;
    setExpandedReferralEntitySlugs((currentExpandedSlugs) => {
      const nextExpandedSlugs = new Set(currentExpandedSlugs);
      if (nextExpandedSlugs.has(slug)) {
        nextExpandedSlugs.delete(slug);
      } else {
        nextExpandedSlugs.add(slug);
      }
      return nextExpandedSlugs;
    });
  }, []);

  const onOpenLabelModal = useCallback((record) => {
    open(buildOpenLabelModalPayload(record));
  }, [open]);

  const onAddLabelFilter = useCallback((labelId) => {
    const nextFilter = buildLabelFilterQuery(location?.search || '', labelId);
    if (!nextFilter) return;

    trackReferralFilterChanged({
      filterKey: 'tag',
      operation: nextFilter.operation,
      source: 'label_chip',
      valueCount: nextFilter.valueCount,
      hasValue: true,
    });

    if (!history?.push || !getReferralRecordsWithQuery) return;
    const pathname = history?.location?.pathname || window.location.pathname;
    history.push(`${pathname}${nextFilter.queryString}`);
    getReferralRecordsWithQuery(nextFilter.queryString);
  }, [getReferralRecordsWithQuery, history, location?.search]);

  return (
    <ReferralRowActionsContext.Provider value={rowActionsRef}>
      {React.createElement(ConnectedReferralManagementRedesign, {
        ...props,
        expandedReferralEntitySlugs,
        onAddLabelFilter,
        onOpenLabelModal,
        onToggleReferralEntity,
        teamsEnabled,
        teams,
        onSetRecordTeam,
        onSetRecordTeamBulk,
      })}
    </ReferralRowActionsContext.Provider>
  );
};

RedesignWithExpansionStateBase.propTypes = {
  getReferralRecordsWithQuery: PropTypes.func,
  history: PropTypes.object,
  location: PropTypes.object,
  openModal: PropTypes.func.isRequired,
  teams: PropTypes.array,
  setRecordTeam: PropTypes.func.isRequired,
  setRecordTeamBulk: PropTypes.func.isRequired,
  rowSelected: PropTypes.func.isRequired,
};

RedesignWithExpansionStateBase.defaultProps = {
  getReferralRecordsWithQuery: () => {},
  history: {},
  location: {},
  teams: [],
};

const RedesignWithExpansionState = connect(
  (state) => ({ teams: state.teams.data }),
  {
    getReferralRecordsWithQuery: getReferralsQuery,
    openModal,
    setRecordTeam,
    setRecordTeamBulk,
    rowSelected,
  },
)(RedesignWithExpansionStateBase);

const ReferralManagement = (props) => {
  // Intentional rollout behavior: use cached localStorage flags for initial paint.
  // We re-check the server-backed flag after mount to converge for this session.
  const redesignAccess = useFeatureAccess(REFERRAL_MANAGEMENT_REDESIGN_FLAG);
  // Assignment controls can fetch provider data, so never expose them from a stale cache entry.
  const referredToProviderEnabled = useFeatureAccess(REFERRED_TO_PROVIDER_FEATURE, false);

  if (!redesignAccess) {
    return (
      <Suspense fallback={null}>
        <LegacyReferralManagement
          {...props}
          referredToProviderEnabled={referredToProviderEnabled}
        />
      </Suspense>
    );
  }
  return React.createElement(RedesignWithExpansionState, {
    ...props,
    referredToProviderEnabled,
  });
};

export default ReferralManagement;
